<template>
  <v-container fluid class="h-100 pa-0">
    <v-row no-gutters v-if="success" class="h-100 d-flex justify-center">
      <v-col>
        <SuccessBillMessage />
      </v-col>
    </v-row>

    <v-row
      no-gutters
      class="h-100 d-flex justify-center align-center"
      v-else-if="hasError && showErrorAlert"
    >
      <v-col>
        <AlertMessage>
          <template #icon>
            <v-icon color="red" size="40"> highlight_off </v-icon>
          </template>
          <template #title>
            <span
              class="text-subtitle-1 text-md-h6 font-weight-bold text-center mb-1"
            >
              Ocurrió un error al cargar la factura
            </span>
          </template>
          <template #description>
            <div
              class="text-subtitle-2 text-md-body-1 font-weight-regular grey--text text--darken-1 text-center"
            >
              <p class="mb-0">
                Para resolverlo, comunicate con el Área de Prestadores por
              </p>
              <p class="mb-0">
                mail a
                <span class="font-weight-bold">expertos@vaitty.com</span>
              </p>
            </div>
          </template>
          <template #actions>
            <div
              class="d-flex w-100 flex-column align-center"
              style="row-gap: 1rem"
            >
              <v-btn color="blue darken-2" class="white--text" large @click="closeErrorAlert">
                Volver a seleccionar servicios
              </v-btn>
              <v-btn
                color="blue darken-2"
                large
                text
                @click="$router.push({ name: 'bill-list' })"
              >
                Volver al inicio
              </v-btn>
            </div>
          </template>
        </AlertMessage>
      </v-col>
    </v-row>

    <v-row no-gutters v-else class="d-flex flex-column h-100">
      <h6 class="text-h6 text-md-h5 font-weight-bold" style="margin-bottom: 10px;">Facturación</h6>
      <div class="left-col">
        <v-alert
          v-if="hasError && showErrorMessage"
          dense
          type="error"
          icon="report"
        >
          {{ showErrorMessage }}
        </v-alert>
        <form>
          <component
            :is="currentStepComponent"
            :formData="formData"
            :v="$v.formData"
            @validateInvoice="validateInvoiceFile"
            :isValidatingInvoice="validatingInvoice"
          />

          <section class="d-flex flex-column" style="row-gap: 1rem">
            <v-btn
              depressed
              large
              block
              type="button"
              v-if="currentStep !== steps.length - 1"
              @click="nextStep"
              :disabled="validationStepHasError"
              :loading="runningInvoiceValidationProcess"
              class="blue darken-2 white--text"
            >
              Siguiente
            </v-btn>

            <p
              v-if="runningInvoiceValidationProcess"
              class="text-center mb-0 text-caption blue--text darken-2 font-weight-regular"
            >
              Verificando datos de la factura...
            </p>

            <v-btn
              depressed
              large
              block
              :loading="sendFormLoading"
              v-if="currentStep === steps.length - 1 && !$v.formData.$invalid"
              color="success"
              type="button"
              @click.prevent="handleSumbit"
            >
              Enviar
            </v-btn>

            <v-btn
              text
              large
              block
              type="button"
              v-if="currentStep > 0"
              @click="currentStep--"
              color="blue darken-2"
              :disabled="runningInvoiceValidationProcess"
              >Anterior</v-btn
            >
          </section>
        </form>
      </div>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import InvoiceSelectionStep from "../components/Form/steps/InvoiceSelectionStep.vue";
import InvoiceValidationStep from "../components/Form/steps/InvoiceValidationStep.vue";
import CBUSelectionStep from "../components/Form/steps/CBUSelectionStep.vue";
import FinalStep from "../components/Form/steps/FinalStep.vue";

import SuccessBillMessage from "../components/SuccessBillMessage.vue";
import AlertMessage from "../components/AlertMessage.vue";

import { required, minLength, maxLength } from "vuelidate/lib/validators";

const WRONG_INVOICE = "illegible_bill";
const WRONG_AMOUNTS = "mismatched_amount";
const DUPLICATE_INVOICE = "invoice_exist";
const CREATE_INVOICE_ERROR = "create_invoice_exception";

const FORM_STEPS = {
  INVOICE_SELECTION: 0,
  INVOICE_VALIDATION: 1,
  CBU_SELECTION: 2,
  FINAL_STEP: 3,
};

export default {
  components: {
    InvoiceSelectionStep,
    InvoiceValidationStep,
    CBUSelectionStep,
    FinalStep,
    SuccessBillMessage,
    AlertMessage,
  },
  data: () => ({
    currentStep: 0,
    steps: [
      InvoiceSelectionStep,
      InvoiceValidationStep,
      CBUSelectionStep,
      FinalStep,
    ],
    success: false,
    hasError: false,
    showErrorAlert: false,
    showErrorMessage: "",
    sendFormLoading: false,
    validatingInvoice: false,
  }),
  beforeMount() {
    this.setState();
  },
  mounted() {
    this.checkForTransactionsInStorage();
    this.$store.commit("set_app_title", "Facturación");
  },
  beforeDestroy() {
    localStorage.removeItem("pending-transactions");
  },
  computed: {
    ...mapState("invoice", ["formData"]),
    ...mapGetters("invoice", ["formDataTransactions"]),

    currentStepComponent() {
      return this.steps[this.currentStep];
    },
    runningInvoiceValidationProcess() {
      return (
        this.currentStep === FORM_STEPS.INVOICE_VALIDATION &&
        this.validatingInvoice
      );
    },
    validationStepHasError() {
      return (
        this.currentStep === FORM_STEPS.INVOICE_VALIDATION && this.hasError
      );
    },
  },
  validations: {
    formData: {
      transactions: {
        required,
        minLength: minLength(1),
        maxLength: maxLength(15),
      },
      cbu: { required },
      invoice: { required },
    },
  },
  methods: {
    ...mapActions("invoice", [
      "handleFormDataChanges",
      "sendInvoice",
      "resetStore",
      "invoiceFileValidation",
    ]),
    ...mapMutations("invoice", ["setState"]),

    checkForTransactionsInStorage() {
      const storageTransactions = localStorage.getItem("pending-transactions");
      const parsedTransactions = storageTransactions
        ? JSON.parse(storageTransactions)
        : [];

      const draft = structuredClone(this.formData);
      draft.transactions = parsedTransactions;

      this.handleFormDataChanges(draft);
    },
    nextStep() {
      if (this.currentStep === FORM_STEPS.INVOICE_SELECTION) {
        this.$v.formData.transactions.$touch();
        if (this.$v.formData.transactions.$invalid) return;
      }
      if (this.currentStep === FORM_STEPS.INVOICE_VALIDATION) {
        this.$v.formData.invoice.$touch();
        if (this.$v.formData.invoice.$invalid || this.validatingInvoice) return;
      }
      if (this.currentStep === FORM_STEPS.CBU_SELECTION) {
        this.$v.formData.cbu.$touch();
        if (this.$v.formData.cbu.$invalid) return;
      }
      this.currentStep++;
    },
    parsePayload(formData = {}) {
      const { invoice, cbu, transactions } = formData;
      const transactionIds = transactions.map((transaction) => transaction.id);
      const form = new FormData();
      form.append("archivo_factura", invoice);
      form.append("transacciones", transactionIds);
      form.append("cbu_field", cbu);
      return form;
    },
    async handleSumbit() {
      if (this.currentStep !== FORM_STEPS.FINAL_STEP) return;
      this.sendFormLoading = true;
      this.$v.formData.$touch();
      if (this.$v.formData.$invalid) return;
      const data = this.parsePayload(this.formData);

      this.sendInvoice(data)
        .then(() => {
          this.success = true;
          this.hasError = false;
          this.showErrorMessage = "";
        })
        .catch((error) => {
          this.handleOcrErrors(error);
        })
        .finally(() => {
          this.sendFormLoading = false;
        });
    },
    async validateInvoiceFile(file) {
      if (this.formData == null) return;
      const { transactions } = this.formData;
      if (file == null || transactions == null || transactions.length === 0)
        return;

      this.validatingInvoice = true;
      this.hasError = false;
      this.showErrorAlert = false;

      const transactionsIds = transactions.map((t) => t.id);
      const formData = new FormData();
      formData.append("archivo_factura", file);
      formData.append("transacciones", transactionsIds);

      this.invoiceFileValidation(formData)
        .then(() => {
          this.showErrorMessage = "";
          this.$store.dispatch("invoice/setInvoiceFile", file);
        })
        .catch((error) => {
          this.handleOcrErrors(error);
        })
        .finally(() => {
          this.validatingInvoice = false;
        });
    },
    handleOcrErrors(error) {
      this.hasError = true;
      if (error.isAxiosError) {
        const errorCode = error.response?.data?.error_code;

        if (errorCode) {
          if (errorCode === WRONG_INVOICE) {
            this.$router.push({ name: "AlternativeFormView" });
          } else if (errorCode === WRONG_AMOUNTS) {
            this.showErrorMessage =
              "El total de la factura no coincide con el indicado, revisá que el monto sea el correcto y volvé a subirla.";
          } else if (errorCode === DUPLICATE_INVOICE) {
            console.log("duplicado");
            this.showErrorMessage =
              "La factura que intentas subir ya está registrada en el sistema. Revisá los datos antes de continuar.";
          } else if (errorCode === CREATE_INVOICE_ERROR) {
            this.showErrorAlert = true;
          }
        } else {
          console.warn(
            "Respuesta de error sin datos o estructura inesperada",
            error
          );
        }
      } else if (error instanceof Error) {
        console.log(error.message);
      } else {
        console.log("Error inesperado al manejar la solicitud", error);
      }
    },
    closeErrorAlert() {
      this.hasError = false;
      this.showErrorAlert = false;

      this.setState();
      this.$v.formData.$reset();

      this.currentStep = FORM_STEPS.INVOICE_SELECTION;
    },
  },
  watch: {
    formData: {
      handler(newValue) {
        this.handleFormDataChanges(newValue);
      },
      deep: true,
    },
    formDataTransactions: {
      handler(newValues) {
        localStorage.setItem("pending-transactions", JSON.stringify(newValues));
      },
      deep: true,
    },
    success(newValue) {
      if (newValue) {
        this.setState();
        localStorage.removeItem("pending-transactions");
      }
    },
  },
};
</script>

<style scoped>
@media (max-width: 959px) {
  .left-col {
    width: 100%;
  }
}

@media (min-width: 960px) {
  .left-col {
    width: 65%;
  }
}
</style>
